import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { TbAlertTriangle } from "react-icons/tb";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import logo from "../assets/cogniflock_logo_full.svg";
import { getApiEndpoint } from "../utils/apiConfig";
import configService from "../utils/configService";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch configuration on component mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const clientId = await configService.getGoogleClientId();
        setGoogleClientId(clientId);
      } catch (error) {
        console.error('Failed to load configuration:', error);
        setError('Failed to load application configuration');
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(
        getApiEndpoint('auth/login'),
        { email, password }
      );
      // Save token (and optionally user role, etc.) to local storage
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      
      // Check if user has completed onboarding
      if (res.data.user.onboardingCompleted && res.data.user.organizations?.length > 0) {
        navigate("/dashboard");
      } else {
        // User exists but hasn't completed onboarding - redirect to onboarding
        navigate("/onboarding");
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "Invalid credentials" ||
          "Error resetting password"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (response) => {
    setLoading(true);
    try {
      const { credential } = response;
      const res = await axios.post(
        getApiEndpoint('auth/google-login'),
        { credential }
      );
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      
      // Check if user has completed onboarding
      if (res.data.user.onboardingCompleted && res.data.user.organizations?.length > 0) {
        navigate("/dashboard");
      } else {
        // User exists but hasn't completed onboarding - redirect to onboarding
        navigate("/onboarding");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error logging in with Google");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleFailure = (response) => {
    console.error(response);
    setError("Google login failed");
  };

  // Show loading state while configuration is being fetched
  if (configLoading || !googleClientId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-gradient-dash">
        <div className="w-full max-w-sm p-6 flex flex-col gap-10">
          <img src={logo} alt="Logo" className="h-6" />
          <div className="w-full max-w-md flex flex-col gap-4 p-6 bg-white-f border border-grey-20 rounded-xl shadow-sm">
            <div className="text-center">
              <div className="inline-block align-middle w-6 h-6 border-2 border-t-2 border-t-brand border-grey-30 rounded-full animate-spin"></div>
              <p className="mt-2 text-sm text-grey-60">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <div className="min-h-screen flex items-center justify-center bg-bg-gradient-dash">
        <div className="w-full max-w-sm p-6 flex flex-col gap-10">
          <img src={logo} alt="Logo" className="h-6" />{" "}
          <div className="relative w-full flex flex-col gap-3">
            <div className="w-full max-w-md flex flex-col gap-4 p-6 bg-white-f border border-grey-20 rounded-xl shadow-sm">
              <div className="text-center">
                <h2 className="text-xl font-normal text-black-0 mb-2">
                  Welcome back!
                </h2>
                <p className="text-sm text-grey-60 mb-4">
                  Sign in to your existing Cogniflock account.
                </p>
              </div>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleFailure}
                render={(renderProps) => (
                  <button
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled || loading}
                    className="!w-full !py-2 !px-4 !bg-white-f !border !border-grey-20 !text-brand !text-sm ! !font-semibold !tracking-wide !rounded-md !hover:grey-30-10 !transition"
                  >
                    {loading ? (
                      <div className="inline-block align-middle w-4 h-4 border-2 border-t-2 border-t-white-f border-bg-opacity rounded-full animate-spin"></div>
                    ) : (
                      "Continue with Google"
                    )}
                  </button>
                )}
              />
              <p className=" font-normal text-center text-sm text-grey-60">
                or{" "}
              </p>
              <form onSubmit={handleLogin} className="flex flex-col gap-7">
                <div className="flex flex-col gap-1">
                  <label className="text-grey-60 text-sm  font-medium">
                    Email
                  </label>
                  <input
                    className="w-full px-3 py-1 border border-grey-30 rounded-md focus:outline-none"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-grey-60 text-sm  font-medium">
                      Password
                    </label>
                    <Link
                      to="/forgot-password"
                      className=" font-semibold text-brand text-sm hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <input
                    className="w-full px-3 py-1 border border-grey-30 rounded-md focus:outline-none"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {/* Password field, other fields, etc. */}
                <button
                  className="w-full py-2 px-4 bg-brand text-white-f text-sm  font-medium tracking-wide rounded-lg border border-brand shadow-inner hover:bg-brand-dark transition"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="inline-block align-middle w-4 h-4 border-2 border-t-2 border-t-white-f border-bg-opacity rounded-full animate-spin"></div>
                  ) : (
                    "Sign In"
                  )}
                </button>
                {error && (
                  <div className="flex gap-2 items-center text-center">
                    <TbAlertTriangle className="size-5 text-red-strong" />{" "}
                    <p className="text-red-strong text-sm  font-normal ">
                      {error}
                    </p>
                  </div>
                )}
              </form>
              <p className="mt-2  font-normal text-center text-sm text-grey-60">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold text-brand hover:underline"
                >
                  Sign up for free
                </Link>
              </p>
            </div>
            {/* Decorative lines */}
            <div className="absolute -top-20 -bottom-8 -left-2 w-px bg-slate-900/[0.1] [mask-image:linear-gradient(to_top,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute -top-20 -right-2 -bottom-8 w-px bg-slate-900/[0.1] [mask-image:linear-gradient(to_top,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute -top-2 -right-20 -left-20 h-px bg-slate-900/[0.1] [mask-image:linear-gradient(to_right,transparent,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute -bottom-2 -right-20 -left-20 h-px bg-slate-900/[0.1] [mask-image:linear-gradient(to_right,transparent,white_4rem,white_calc(100%-4rem),transparent)]"></div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default Login;
